/* HUENIT LAB 2.0 — Admin: 가이드 관리 (카테고리 → 큰 주제 → 작은 주제).
   ★ 소스 = 서버 발행본 + 임시 저장본(draft). 마운트 시 draft 가 있으면 그것을(작업 이어하기),
   없으면 발행본을 편집 트리로 쓴다. 편집하면 자동으로 draft.json 에 임시 저장(디바운스 +
   언마운트 플러시) — 탭 이동/재접속에도 작업본 보존. [발행]해야 사용자(앱)에 반영. */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { GuideIcon } from '../../icons/GuideIcon.jsx';
import { Modal, Confirm, MLText, ColorPicker, IconPicker, useDragSort, BlockEditor, useToast, Spinner, Loading, ErrorState } from '../../components/index.js';
import { useLocal, move, uid, LANG_SEED } from '../../data/store.js';
import { mlGet } from '../../lib/ml.js';
import { tint, PALETTE } from '../../config/palette.js';
import { useSession } from '../../auth/session.jsx';
import { loadGuideTree, loadPublished, diffDraftVsPublished, publish, loadDraft, saveDraft, deleteDraft } from './guideRepo.js';
import { fmtDateTime } from '../../lib/datetime.js';

/* 발행 상태 배지 — NEW=신규, CHANGED=변경, CURRENT=발행됨. 기존 디자인 토큰만 사용. */
const PUB_BADGE = {
  NEW:     { label: '신규', bg: 'var(--brand-50)',  fg: 'var(--brand-600)' },
  CHANGED: { label: '변경', bg: 'var(--warn-bg)',   fg: 'var(--warn)' },
  CURRENT: { label: '발행됨', bg: 'var(--ok-bg)',    fg: 'var(--ok)' },
};
function PubBadge({ state }) {
  const b = state && PUB_BADGE[state];
  if (!b) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, background: b.bg, color: b.fg, flexShrink: 0 }}>
      {b.label}
    </span>
  );
}

/* 작은 주제 리스트 (자체 드래그 정렬) */
function SmallList({ items, defLang, pubById, onReorder, onEdit, onDelete }) {
  const bind = useDragSort(onReorder);
  if (items.length === 0) return <div style={{ fontSize: 12.5, color: 'var(--ink-400)', padding: '4px 2px 8px' }}>작은 주제가 없습니다. 아래에서 추가하세요.</div>;
  return (
    <div>
      {items.map((s, i) => {
        const d = bind(i);
        const nBlocks = (s.content && s.content[defLang] || []).length;
        return (
          <div key={s.id} className={"small-row" + (d.isDragging ? " is-dragging" : "") + (d.isOver ? " is-dragover" : "")}
               onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
            <span className="row__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd}><Icon name="menu" size={15} /></span>
            <span className="small-row__no mono">{String(i + 1).padStart(2, '0')}</span>
            <div className="small-row__main">
              <div className="small-row__title">{mlGet(s.title, defLang) || '(제목 없음)'}<PubBadge state={pubById && pubById[s.id]} /></div>
              <div className="small-row__sub"><Icon name="file" size={12} />{nBlocks}개 블록</div>
            </div>
            <div className="row__actions">
              <button className="icon-btn" onClick={() => onEdit(s)} title="내용 편집"><Icon name="file" size={15} /></button>
              <button className="icon-btn icon-btn--danger" onClick={() => onDelete(s)} title="삭제"><Icon name="trash" size={15} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* 작은 주제 내용 편집 모달 */
function SmallModal({ draft, setDraft, langs, onSave, onClose, isNew }) {
  const def = (langs.find(l => l.default) || langs[0]).code;
  const [clang, setClang] = useState(def);
  const content = draft.content || {};
  const blocks = content[clang] || [];
  const setBlocks = (next) => setDraft({ ...draft, content: { ...content, [clang]: next } });
  const canSave = (draft.title[def] || '').trim();
  return (
    <Modal wide title={isNew ? '작은 주제 추가' : '작은 주제 편집'} sub="제목과 내용을 언어별로 작성할 수 있어요." onClose={onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={onSave}>저장</button>
      </>}>
      <div className="field">
        <label className="field__label">제목<span className="req">*</span></label>
        <MLText value={draft.title} onChange={v => setDraft({ ...draft, title: v })} langs={langs} placeholder="작은 주제 제목" />
      </div>
      <div className="field">
        <label className="field__label">내용</label>
        <div className="langtabs">
          {langs.map(l => (
            <button key={l.code} type="button" className={"langtab" + (clang === l.code ? " is-on" : "")} onClick={() => setClang(l.code)}>
              <span className={"langtab__dot" + ((content[l.code] || []).length ? " is-filled" : "")}></span>{l.label}
            </button>
          ))}
        </div>
        <BlockEditor blocks={blocks} onChange={setBlocks} />
        <div className="field__hint">제목/내용 · 블록은 드래그로 순서를 바꿀 수 있어요. 언어 탭마다 따로 작성됩니다.</div>
      </div>
    </Modal>
  );
}

/* 큰 주제 카드 */
function BigCard({ big, defLang, langs, pubById, onEditBig, onDeleteBig, onAddSmall, onEditSmall, onDeleteSmall, onReorderSmall, dragBind, idx }) {
  const d = dragBind(idx);
  return (
    <div className={"big-card" + (d.isOver ? " is-dragover" : "")} onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
      <div className="big-card__head">
        <span className="row__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd}><Icon name="menu" size={16} /></span>
        <span className="icontile" style={{ background: tint(big._color), color: big._color }}><GuideIcon name={big.icon} size={22} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="big-card__title">{mlGet(big.label, defLang) || '(이름 없음)'}</div>
          <div className="big-card__meta">큰 주제 · 작은 주제 {big.small.length}개</div>
        </div>
        <button className="icon-btn" onClick={() => onEditBig(big)} title="수정"><Icon name="file" size={15} /></button>
        <button className="icon-btn icon-btn--danger" onClick={() => onDeleteBig(big)} title="삭제"><Icon name="trash" size={15} /></button>
      </div>
      <div className="big-card__body">
        <SmallList items={big.small} defLang={defLang} pubById={pubById}
          onReorder={(f, t) => onReorderSmall(big.id, f, t)}
          onEdit={(s) => onEditSmall(big.id, s)}
          onDelete={(s) => onDeleteSmall(big.id, s)} />
        <button className="btn btn--ghost btn--sm" style={{ marginTop: 6 }} onClick={() => onAddSmall(big.id)}><Icon name="plus" size={14} stroke={2.2} />작은 주제 추가</button>
      </div>
    </div>
  );
}

export function GuideAdmin() {
  const toast = useToast();
  const { getCredentials } = useSession();
  const [langs] = useLocal('adm.langs', LANG_SEED);
  const def = (langs.find(l => l.default) || langs[0]).code;

  /* ---- 서버에서 로드한 편집 트리(현재 서버 상태) ---- */
  const [guide, setGuide] = useState(null);     // 로드 전 null
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [sel, setSel] = useState(null);

  const [diff, setDiff] = useState(null);        // { byId, removed, counts }
  const [pubStep, setPubStep] = useState(null);  // 진행 라벨(발행 중) | null
  const [autoSave, setAutoSave] = useState(null); // null | {state:'saving'|'saved'|'error', at?}
  const lastSavedRef = useRef('');                // 마지막 저장/로드 시점의 트리 직렬화 스냅샷
  const guideLatestRef = useRef(null);            // 언마운트 플러시용 최신 트리
  const pubStepRef = useRef(null);

  const [catModal, setCatModal] = useState(null);   // {id|'new'}
  const [catDraft, setCatDraft] = useState(null);
  const [bigModal, setBigModal] = useState(null);    // {id|'new'}
  const [bigDraft, setBigDraft] = useState(null);
  const [smallModal, setSmallModal] = useState(null);// {bigId, id|'new'}
  const [smallDraft, setSmallDraft] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const cats = guide || [];
  const selCat = cats.find(c => c.id === sel) || cats[0];

  /* 서버 → 편집 트리. draft(임시 저장본)가 있으면 그것을, 없으면 발행본을 사용.
     discardDraft = 작업본 폐기 후 발행본 강제 로드(새로고침). 마운트/새로고침/발행후 호출. */
  const reloadFromServer = useCallback(async ({ discardDraft = false } = {}) => {
    setLoading(true); setLoadErr(null);
    try {
      if (discardDraft) await deleteDraft(getCredentials);
      let tree = null;
      let fromDraft = null;                                 // draft 메타({saved_at}) | null
      if (!discardDraft) {
        try {
          const draft = await loadDraft();
          if (draft) { tree = draft.tree; fromDraft = draft; }
        } catch (e) { /* draft 읽기 실패 → 발행본으로 진행(자동 저장이 다시 만듦) */ }
      }
      if (!tree) tree = await loadGuideTree();
      lastSavedRef.current = JSON.stringify(tree);          // 로드 직후는 저장할 것 없음
      setAutoSave(fromDraft ? { state: 'saved', at: fromDraft.saved_at } : null);
      setGuide(tree);
      setSel((prev) => (tree.find(c => c.id === prev) ? prev : (tree[0] ? tree[0].id : null)));
      if (fromDraft) toast('임시 저장본을 불러왔습니다 — 발행 전 작업을 이어서 편집하세요');
    } catch (e) {
      setLoadErr(e);
    } finally {
      setLoading(false);
    }
  }, [getCredentials, toast]);

  useEffect(() => { reloadFromServer(); }, [reloadFromServer]);

  /* ---- 자동 임시 저장 — 편집 후 1.2s 디바운스로 draft.json PUT ---- */
  useEffect(() => { guideLatestRef.current = guide; }, [guide]);
  useEffect(() => { pubStepRef.current = pubStep; }, [pubStep]);
  useEffect(() => {
    if (!guide || loading || pubStep) return;
    const snap = JSON.stringify(guide);
    if (snap === lastSavedRef.current) return;
    setAutoSave({ state: 'saving' });
    const t = setTimeout(async () => {
      try {
        const at = await saveDraft(getCredentials, guide);
        lastSavedRef.current = snap;
        setAutoSave({ state: 'saved', at });
      } catch (e) {
        setAutoSave({ state: 'error' });
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [guide, loading, pubStep, getCredentials]);
  /* 언마운트(탭 이동) 시 미저장분 즉시 플러시 — 디바운스 대기 중 이탈해도 유실 없음. */
  useEffect(() => () => {
    const g = guideLatestRef.current;
    if (!g || pubStepRef.current) return;
    if (JSON.stringify(g) !== lastSavedRef.current) saveDraft(getCredentials, g).catch(() => {});
  }, [getCredentials]);

  /* 편집 트리 ↔ 발행본 diff → 카테고리/작은주제 배지(신규/변경/발행됨). */
  useEffect(() => {
    if (!guide) return;
    let cancelled = false;
    (async () => {
      try {
        const pub = await loadPublished();
        const d = await diffDraftVsPublished(guide, langs, pub);
        if (!cancelled) setDiff(d);
      } catch (e) {
        if (!cancelled) setDiff(null);
      }
    })();
    return () => { cancelled = true; };
  }, [guide, langs]);

  const pubById = (diff && diff.byId) || null;
  // 카테고리 롤업 배지 — 자식 작은주제에 NEW/CHANGED 있으면 그 상태로 표식.
  const catBadge = (cat) => {
    if (!pubById) return null;
    let hasNew = false, hasChanged = false;
    for (const b of cat.big) for (const s of b.small) {
      const st = pubById[s.id];
      if (st === 'CHANGED') hasChanged = true;
      else if (st === 'NEW') hasNew = true;
    }
    return hasChanged ? 'CHANGED' : hasNew ? 'NEW' : null;
  };

  const doPublish = async () => {
    if (pubStep || !guide) return;
    try {
      const res = await publish(getCredentials, { draftTree: guide, langs, onStep: setPubStep });
      setPubStep(null);
      toast(res.changed.length ? `발행 완료 — ${res.changed.length}개 주제 갱신` : '발행 완료 — 변경 없음');
      await reloadFromServer();   // 발행 후 서버에서 다시 로드 → 트리·배지 정합
    } catch (e) {
      setPubStep(null);
      toast((e && e.message) || '발행에 실패했습니다.', 'error');
    }
  };

  // 서버 발행본으로 새로고침 — 임시 저장본 폐기. 미발행 변경이 있으면 확인.
  const refresh = () => {
    if (pubStep) return;
    if (diff && (diff.counts.neww || diff.counts.changed)) {
      setConfirm({ t: '서버 발행본을 다시 불러올까요?', b: '임시 저장본이 삭제되고, 발행하지 않은 변경사항이 사라집니다.', fn: () => reloadFromServer({ discardDraft: true }) });
    } else {
      reloadFromServer({ discardDraft: true });
    }
  };

  /* ---- 불변 업데이트 helper ---- */
  const updCat = (catId, fn) => setGuide(cats.map(c => c.id === catId ? fn(c) : c));
  const updBig = (catId, bigId, fn) => updCat(catId, c => ({ ...c, big: c.big.map(b => b.id === bigId ? fn(b) : b) }));

  /* ---- 카테고리 ---- */
  const catBind = useDragSort((f, t) => setGuide(move(cats, f, t)));
  const newCat = () => { setCatDraft({ id: uid('g'), color: PALETTE[cats.length % PALETTE.length], label: {}, big: [] }); setCatModal({ id: 'new' }); };
  const editCat = (c) => { setCatDraft(JSON.parse(JSON.stringify(c))); setCatModal({ id: c.id }); };
  const saveCat = () => {
    if (catModal.id === 'new') { setGuide([...cats, catDraft]); setSel(catDraft.id); toast('카테고리를 추가했습니다'); }
    else { updCat(catModal.id, () => catDraft); toast('카테고리를 수정했습니다'); }
    setCatModal(null); setCatDraft(null);
  };
  const delCat = (c) => setConfirm({ t: '카테고리를 삭제할까요?', b: <><b>{mlGet(c.label, def)}</b> 카테고리와 하위 모든 주제가 삭제됩니다.</>, fn: () => { const next = cats.filter(x => x.id !== c.id); setGuide(next); if (sel === c.id) setSel(next[0] ? next[0].id : null); } });

  /* ---- 큰 주제 ---- */
  const bigBind = useDragSort((f, t) => selCat && updCat(selCat.id, c => ({ ...c, big: move(c.big, f, t) })));
  const newBig = () => { setBigDraft({ id: uid('bg'), icon: 'flag', label: {}, small: [] }); setBigModal({ id: 'new' }); };
  const editBig = (b) => { setBigDraft(JSON.parse(JSON.stringify(b))); setBigModal({ id: b.id }); };
  const saveBig = () => {
    if (bigModal.id === 'new') updCat(selCat.id, c => ({ ...c, big: [...c.big, bigDraft] }));
    else updBig(selCat.id, bigModal.id, () => bigDraft);
    toast('큰 주제를 저장했습니다'); setBigModal(null); setBigDraft(null);
  };
  const delBig = (b) => setConfirm({ t: '큰 주제를 삭제할까요?', b: <><b>{mlGet(b.label, def)}</b>와 하위 작은 주제가 모두 삭제됩니다.</>, fn: () => updCat(selCat.id, c => ({ ...c, big: c.big.filter(x => x.id !== b.id) })) });

  /* ---- 작은 주제 ---- */
  const newSmall = (bigId) => { setSmallDraft({ id: uid('sm'), title: {}, content: {} }); setSmallModal({ bigId, id: 'new' }); };
  const editSmall = (bigId, s) => { setSmallDraft(JSON.parse(JSON.stringify(s))); setSmallModal({ bigId, id: s.id }); };
  const saveSmall = () => {
    const { bigId, id } = smallModal;
    updBig(selCat.id, bigId, b => ({ ...b, small: id === 'new' ? [...b.small, smallDraft] : b.small.map(s => s.id === id ? smallDraft : s) }));
    toast('작은 주제를 저장했습니다'); setSmallModal(null); setSmallDraft(null);
  };
  const delSmall = (bigId, s) => setConfirm({ t: '작은 주제를 삭제할까요?', b: <><b>{mlGet(s.title, def)}</b> 내용이 삭제됩니다.</>, fn: () => updBig(selCat.id, bigId, b => ({ ...b, small: b.small.filter(x => x.id !== s.id) })) });
  const reorderSmall = (bigId, f, t) => updBig(selCat.id, bigId, b => ({ ...b, small: move(b.small, f, t) }));

  /* ---- 로딩/에러 게이트 (모든 hook 호출 뒤) ---- */
  if (loading) return <Loading title="가이드를 불러오는 중…" sub="서버의 현재 발행본을 가져옵니다." />;
  if (loadErr) return <ErrorState error={loadErr} title="가이드를 불러오지 못했습니다" onRetry={reloadFromServer} />;

  return (
    <div>
      <div className="toolbar">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--ink-500)', fontWeight: 500 }}>
            수정하면 자동으로 서버에 임시 저장됩니다. 사용자(앱)에는 [발행]해야 반영됩니다.
          </span>
          {autoSave && autoSave.state === 'saving' && (
            <span className="chip" style={{ background: 'var(--ink-100)', color: 'var(--ink-500)' }}>
              <Spinner size={12} stroke={2} />임시 저장 중…
            </span>
          )}
          {autoSave && autoSave.state === 'saved' && (
            <span className="chip" style={{ background: 'var(--ink-100)', color: 'var(--ink-500)' }} title={'임시 저장본은 발행 전까지 사용자에게 보이지 않습니다. ' + (autoSave.at ? fmtDateTime(autoSave.at) : '')}>
              <Icon name="save" size={12} />임시 저장됨{autoSave.at ? ' · ' + fmtDateTime(autoSave.at).slice(11) : ''}
            </span>
          )}
          {autoSave && autoSave.state === 'error' && (
            <span className="chip" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }} title="네트워크/권한을 확인하세요. 편집을 계속하면 다시 시도합니다.">
              <Icon name="bell" size={12} />임시 저장 실패
            </span>
          )}
          {pubStep ? (
            <span className="chip" style={{ background: 'var(--brand-50)', color: 'var(--brand-600)' }}>
              <Spinner size={12} stroke={2} />{pubStep}
            </span>
          ) : diff && (diff.counts.neww || diff.counts.changed) ? (
            <span className="chip" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }} title="발행 시 신규/변경 주제가 앱으로 전송됩니다.">
              <Icon name="upload" size={13} />발행 대기 · 신규 {diff.counts.neww} · 변경 {diff.counts.changed}
            </span>
          ) : diff ? (
            <span className="chip" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}>
              <Icon name="check" size={13} />발행본과 동일
            </span>
          ) : null}
        </div>
        <button className="btn btn--ghost" onClick={refresh} disabled={!!pubStep} title="서버의 현재 발행본을 다시 불러옵니다"><Icon name="refresh" size={15} />새로고침</button>
        <button className="btn btn--ghost" onClick={newCat}><Icon name="plus" size={15} stroke={2.2} />카테고리 추가</button>
        <button className="btn btn--primary" disabled={!!pubStep} onClick={doPublish}><Icon name="upload" size={15} stroke={2.2} />{pubStep ? '발행 중…' : '발행'}</button>
      </div>

      <div className="gtree">
        {/* 좌: 카테고리 레일 */}
        <div className="gtree__rail">
          {cats.map((c, i) => {
            const d = catBind(i);
            const small = c.big.reduce((n, b) => n + b.small.length, 0);
            return (
              <div key={c.id} className={"cat-card" + (sel === c.id ? " is-on" : "") + (d.isOver ? " is-dragover" : "")}
                   onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
                <div className="cat-card__head" onClick={() => setSel(c.id)}>
                  <span className="row__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd}><Icon name="menu" size={15} /></span>
                  <span className="cat-card__bar" style={{ background: c.color }}></span>
                  <span className="cat-card__name">{mlGet(c.label, def) || '(이름 없음)'}</span>
                  <PubBadge state={catBadge(c)} />
                  <span className="cat-card__count">{c.big.length}·{small}</span>
                  <button className="icon-btn icon-btn--ghost" onClick={(e) => { e.stopPropagation(); editCat(c); }} title="수정"><Icon name="file" size={14} /></button>
                  <button className="icon-btn icon-btn--ghost icon-btn--danger" onClick={(e) => { e.stopPropagation(); delCat(c); }} title="삭제"><Icon name="trash" size={14} /></button>
                </div>
              </div>
            );
          })}
          {cats.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-400)', padding: '12px 4px' }}>발행된 카테고리가 없습니다. [카테고리 추가]로 시작하세요.</div>}
        </div>

        {/* 우: 선택 카테고리 상세 */}
        <div className="gtree__detail">
          {!selCat ? (
            <div className="empty-state"><div className="empty-state__icon"><Icon name="book" size={26} /></div><div className="empty-state__t">카테고리를 선택하세요</div></div>
          ) : (
            <>
              <div className="panel" style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="cat-card__bar" style={{ background: selCat.color, width: 6, height: 26, borderRadius: 3 }}></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' }}>{mlGet(selCat.label, def)}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-400)', fontWeight: 600 }}>큰 주제 {selCat.big.length}개</div>
                  </div>
                  <button className="btn btn--soft btn--sm" onClick={newBig}><Icon name="plus" size={14} stroke={2.2} />큰 주제 추가</button>
                </div>
              </div>
              {selCat.big.map((b, i) => (
                <BigCard key={b.id} big={{ ...b, _color: selCat.color }} idx={i} dragBind={bigBind} defLang={def} langs={langs} pubById={pubById}
                  onEditBig={editBig} onDeleteBig={delBig}
                  onAddSmall={newSmall} onEditSmall={editSmall} onDeleteSmall={delSmall} onReorderSmall={reorderSmall} />
              ))}
              {selCat.big.length === 0 && (
                <div className="empty-state"><div className="empty-state__icon"><GuideIcon name="flag" size={24} /></div><div className="empty-state__t">큰 주제가 없습니다</div><div className="empty-state__d">[큰 주제 추가]로 시작하세요.</div></div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 카테고리 모달 */}
      {catModal && catDraft && (
        <Modal title={catModal.id === 'new' ? '카테고리 추가' : '카테고리 수정'} onClose={() => { setCatModal(null); setCatDraft(null); }}
          foot={<><button className="btn btn--ghost" onClick={() => { setCatModal(null); setCatDraft(null); }}>취소</button><button className="btn btn--primary" disabled={!(catDraft.label[def] || '').trim()} onClick={saveCat}>저장</button></>}>
          <div className="field"><label className="field__label">카테고리 이름<span className="req">*</span></label><MLText value={catDraft.label} onChange={v => setCatDraft({ ...catDraft, label: v })} langs={langs} placeholder="예: 시작하기" /></div>
          <div className="field"><label className="field__label">색상</label><ColorPicker value={catDraft.color} onChange={c => setCatDraft({ ...catDraft, color: c })} /></div>
        </Modal>
      )}

      {/* 큰 주제 모달 */}
      {bigModal && bigDraft && (
        <Modal title={bigModal.id === 'new' ? '큰 주제 추가' : '큰 주제 수정'} onClose={() => { setBigModal(null); setBigDraft(null); }}
          foot={<><button className="btn btn--ghost" onClick={() => { setBigModal(null); setBigDraft(null); }}>취소</button><button className="btn btn--primary" disabled={!(bigDraft.label[def] || '').trim()} onClick={saveBig}>저장</button></>}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 18 }}>
            <span className="icontile" style={{ width: 56, height: 56, borderRadius: 16, background: tint(selCat.color), color: selCat.color }}><GuideIcon name={bigDraft.icon} size={28} /></span>
            <div style={{ fontSize: 12.5, color: 'var(--ink-500)' }}>아이콘과 이름을 선택하세요.<br />색상은 카테고리 색을 따릅니다.</div>
          </div>
          <div className="field"><label className="field__label">큰 주제 이름<span className="req">*</span></label><MLText value={bigDraft.label} onChange={v => setBigDraft({ ...bigDraft, label: v })} langs={langs} placeholder="예: 입문 가이드" /></div>
          <div className="field"><label className="field__label">아이콘</label><IconPicker value={bigDraft.icon} onChange={ic => setBigDraft({ ...bigDraft, icon: ic })} /></div>
        </Modal>
      )}

      {/* 작은 주제 모달 */}
      {smallModal && smallDraft && (
        <SmallModal draft={smallDraft} setDraft={setSmallDraft} langs={langs} isNew={smallModal.id === 'new'} onSave={saveSmall} onClose={() => { setSmallModal(null); setSmallDraft(null); }} />
      )}

      {confirm && <Confirm title={confirm.t} body={confirm.b} onConfirm={() => { confirm.fn(); setConfirm(null); }} onClose={() => setConfirm(null)} />}
    </div>
  );
}

export default GuideAdmin;
